// Fortera Scrubs Order — Set Type + Size quantity picker (Unisex, Asia Size)
// Filters Size table by selected Set Type. No Gender split.
// Integrates with existing fortera-bulk-customily-bridge.js cart submission flow.

(function () {
  if (customElements.get('fortera-scrubs-order')) return;

  class ForteraScrubsOrder extends HTMLElement {
    constructor() {
      super();
      this._initialized = false;
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      // Parse data attributes
      this.setTypeIndex = parseInt(this.dataset.setTypeIndex);
      this.sizeIndex = parseInt(this.dataset.sizeIndex);
      this.hasSetType = this.dataset.hasSetType === 'true';

      // Parse variant data
      const variantData = this.querySelector('[data-scrubs-variants]');
      this.variants = variantData ? JSON.parse(variantData.textContent) : [];

      // Cache DOM elements
      this.rows = this.querySelectorAll('.fortera-scrubs-order__row');
      this.totalQtyEl = this.querySelector('[data-scrubs-total-qty]');
      this.totalPriceEl = this.querySelector('[data-scrubs-total-price]');
      this.submitBtn = this.querySelector('[data-scrubs-submit]');
      this.setTypeNameEl = this.querySelector('[data-scrubs-set-type-name]');

      // Selectors
      this.setTypeButtons = this.querySelectorAll('[data-set-type]');

      // Initial selection (first button if multi-set-type)
      this.selectedSetType = this.hasSetType ? (this.setTypeButtons[0]?.dataset.setType || null) : null;

      if (this.hasSetType) this.bindSetTypeButtons();
      this.bindQuantityInputs();

      // Initial render
      this.updateSizeTable();

      // Customily app loads after our connectedCallback and overwrites the top
      // price element to $0 (it treats .price-item--regular as a customization
      // total). Re-assert the correct price after each Customily mutation, then
      // disconnect once stable to avoid an infinite loop.
      this.watchSectionPrice();
    }

    watchSectionPrice() {
      const priceContainer = document.querySelector('[id^="price-template--"]');
      if (!priceContainer) return;
      let lastApplied = '';
      let stableTicks = 0;
      const reapply = () => {
        this.updateSectionPrice();
        const current = priceContainer.querySelector('.price-item--regular')?.textContent || '';
        if (current === lastApplied) {
          stableTicks++;
          if (stableTicks >= 3) obs.disconnect();
        } else {
          stableTicks = 0;
          lastApplied = current;
        }
      };
      const obs = new MutationObserver(() => {
        // Throttle to next frame so we run AFTER Customily finishes its write
        requestAnimationFrame(reapply);
      });
      obs.observe(priceContainer, { childList: true, subtree: true, characterData: true });
      // Also re-apply on a handful of delayed ticks in case Customily writes
      // before any mutation handler is attached.
      [100, 300, 800, 1500, 3000].forEach((ms) => setTimeout(reapply, ms));
    }

    bindSetTypeButtons() {
      this.setTypeButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          this.setTypeButtons.forEach((b) => b.classList.remove('is-active'));
          btn.classList.add('is-active');
          this.selectedSetType = btn.dataset.setType;
          if (this.setTypeNameEl) this.setTypeNameEl.textContent = this.selectedSetType;
          this.resetQuantities();
          this.updateSizeTable();
          this.updateTotals();
        });
      });
    }

    bindQuantityInputs() {
      this.rows.forEach((row) => {
        const minusBtn = row.querySelector('[data-action="minus"]');
        const plusBtn = row.querySelector('[data-action="plus"]');
        const input = row.querySelector('.fortera-bulk-order__input');

        minusBtn.addEventListener('click', () => {
          const val = parseInt(input.value) || 0;
          input.value = Math.max(0, val - 1);
          this.updateTotals();
        });

        plusBtn.addEventListener('click', () => {
          const val = parseInt(input.value) || 0;
          input.value = val + 1;
          this.updateTotals();
        });

        input.addEventListener('change', () => {
          const val = parseInt(input.value) || 0;
          input.value = Math.max(0, val);
          this.updateTotals();
        });

        input.addEventListener('focus', () => input.select());
      });
    }

    // Find variant matching selected Set Type + Size
    findVariant(size) {
      return this.variants.find((v) => {
        const matchSize = v.options[this.sizeIndex] === size;
        const matchSetType = !this.hasSetType || v.options[this.setTypeIndex] === this.selectedSetType;
        return matchSize && matchSetType;
      });
    }

    // Show/hide rows based on whether variant exists for current selection.
    // Also keep row-level data attributes (data-variant-id, data-price,
    // data-base-price, data-available) in sync so they match the schema used by
    // fortera-bulk-order rows — this is what fortera-bulk-customily-bridge.js
    // reads to render the top-of-page price block.
    updateSizeTable() {
      this.rows.forEach((row) => {
        const size = row.dataset.size;
        const variant = this.findVariant(size);
        const input = row.querySelector('.fortera-bulk-order__input');
        const priceEl = row.querySelector('[data-scrubs-price]');

        if (variant && variant.available) {
          row.hidden = false;
          row.dataset.variantId = variant.id;
          row.dataset.price = variant.price;
          row.dataset.basePrice = variant.price;
          row.dataset.available = 'true';
          input.dataset.scrubsVariantId = variant.id;
          input.dataset.scrubsPrice = variant.price;
          if (priceEl) priceEl.textContent = variant.price_formatted;
        } else {
          row.hidden = true;
          row.dataset.available = 'false';
          input.dataset.scrubsVariantId = '';
        }
      });
      this.updateSectionPrice();
    }

    // Update the top-of-page price block to match the cheapest available row
    // for the current Set Type selection. Mirrors what bulk-customily-bridge does
    // for polos when the surcharge changes.
    updateSectionPrice() {
      const priceEl =
        document.querySelector('[id^="price-"] .price-item--regular') ||
        document.querySelector('[id^="price-"] .price-item--sale');
      if (!priceEl) return;
      const availableRow = this.querySelector('.fortera-scrubs-order__row[data-available="true"]');
      if (!availableRow) return;
      const cents = parseInt(availableRow.dataset.basePrice) || 0;
      priceEl.textContent = (cents / 100).toLocaleString('en-US', {
        style: 'currency', currency: 'USD',
      });
    }

    resetQuantities() {
      this.rows.forEach((row) => {
        const input = row.querySelector('.fortera-bulk-order__input');
        input.value = 0;
      });
    }

    updateTotals() {
      let totalQty = 0;
      let totalPrice = 0;
      this.rows.forEach((row) => {
        if (row.hidden) return;
        const input = row.querySelector('.fortera-bulk-order__input');
        const qty = parseInt(input.value) || 0;
        const price = parseInt(input.dataset.scrubsPrice) || 0;
        totalQty += qty;
        totalPrice += qty * price;
      });
      if (this.totalQtyEl) this.totalQtyEl.textContent = totalQty;
      if (this.totalPriceEl) this.totalPriceEl.textContent = this.formatMoney(totalPrice);
    }

    formatMoney(cents) {
      const dollars = (cents / 100).toFixed(2);
      return '$' + dollars;
    }

    // Public method (matches fortera-bulk-order API) so bridge.js can pick up items uniformly
    getItems() {
      const items = [];
      this.rows.forEach((row) => {
        if (row.hidden) return;
        const input = row.querySelector('.fortera-bulk-order__input');
        const qty = parseInt(input.value) || 0;
        const variantId = input.dataset.scrubsVariantId;
        if (qty > 0 && variantId) {
          items.push({ id: parseInt(variantId), quantity: qty });
        }
      });
      return items;
    }

    // Match bulk-order API (no-op for scrubs since no Customily surcharge)
    setSurcharge(_s) { /* not applicable for scrubs */ }
  }

  customElements.define('fortera-scrubs-order', ForteraScrubsOrder);
})();
